import { Permissions } from '../../../../models/enums/permissions';
import { cloneDeep } from 'lodash';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { getDocumentHelper } from '../../../../actions/utility-actions';
import ResultDialog from '../../../../components/dialog-types/ResultDialog';
import { PageBody } from '../../../../components/page-components/PageBody';
import { PageContent } from '../../../../components/page-components/PageContent';
import { PageHeading } from '../../../../components/page-components/PageHeading';
import { HasPermission } from '../../../../containers/authorization-helpers/HasPermission';
import { ApiResponse, StatusCode } from '../../../../utils/api';
import { PageNames } from '../../../../utils/app-constants';
import { AppUtils } from '../../../../utils/app-utils';
import { MetricTable } from '../../../../production/components/MetricTable';
import { MetricTableLegend } from '../../../../production/components/MetricTableLegend';
import { MetricTotals } from '../../../../production/components/MetricTotals';
import { MetricsDetailFlyout } from '../../../../production/components/MetricsDetailFlyout';
import { InsertMethod } from '../../../../production/models/enums/insert-method';
import { ScheduleGroup } from '../../../../production/models/enums/schedule-group';
import { PlanningType } from '../../../../production/models/enums/planning-type';
import { ProductionJobStatus } from '../../../../production/models/enums/production-job-status';
import { ProductionJobType } from '../../../../production/models/enums/production-job-type';
import { UnplannedDeliveryOrder } from '../../../../production/models/metrics/unplanned-delivery-order';
import { DocumentProductionPaperwork } from '../../../../production/models/schedule/document-paperwork';
import { DocumentProductionPaperworkByLine } from '../../../../production/models/schedule/document-paperwork-by-line';
import { ProductionJobUpdate } from '../../../../production/models/schedule/production-job-update';
import { ProductionJobUpdateResult } from '../../../../production/models/schedule/production-job-update-result';
import { ProductionRun } from '../../../../production/models/schedule/production-run';
import { ProductionScheduleExport } from '../../../../production/models/schedule/schedule-export-document';
import { CellMetric, CellType } from '../../../../production/models/shared/metric-base';
import { MetricRunningTotal } from '../../../../production/models/shared/metric-running-total';
import { ProductionSvc } from '../../../../production/service';
import '../../../../production/styles.scss';

interface IProps {
    /**
     * Which of the two schedules this is.  The CD/OEM one shows the metrics of its runs and what is done with them,
     * the RS one shows only the MISC metric of its runs, which is all they have.
     */
    group: ScheduleGroup;
}

export const ProductionSchedule = (props : IProps) => {

    const showMetrics = props.group === 'cdoem';
    const dispatch = useDispatch();
    const [service] = React.useState(new ProductionSvc());
    const [selecteds, setSelecteds] = React.useState<ProductionRun[]>([]);
    const [selectedTotal, setSelectedTotal] = React.useState<MetricRunningTotal>(new MetricRunningTotal());
    const [runs, setRuns] = React.useState<ProductionRun[]>([]);
    const [cellData, setCellData] = React.useState<any[]>([]);
    const [showRowTotal, setShowRowTotal] = React.useState<boolean>(false);
    const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);
    const [orders, setOrders] = React.useState<UnplannedDeliveryOrder[]>([]);
    const [flyoutTitle, setFlyoutTitle] = React.useState<string>('');

    const parseCells = (data : any[]) => {
        const celltable : any[] = [];
        data.forEach((itm : ProductionRun) => {
            celltable.push([...itm.METRIC_LIST]);
        });

        setCellData(celltable);
    };

    async function getSchedule() {
        const res = await service.getProductionSchedule(props.group);
        if (res.status === StatusCode.Success) {
            const cloned = cloneDeep(res.data);
            setRuns(cloned);
            parseCells(cloned);
        }
    }

    /**
     * Fetch our data once on page load
     */
    React.useEffect(() => {
        getSchedule();
    }, []);

    /**
     * Close the detail flyout
     */
    const closeDetailFlyout = async () => {
        setIsDetailsOpen(false);
    };
    
    /**
     * Open the order detail flyout to show the list of 
     * orders on the run
     */
    const openDetailFlyout = async () => {

        if (!selecteds || selecteds.length > 1) {
            return;
        }

        const rID = (selecteds[0] as ProductionRun).id;
        const rName = (selecteds[0] as ProductionRun).name;

        const results = await service.getProductionRunOrders(rID);
        if (results.status === StatusCode.Success) {
            setOrders(results.data);

            const title = `${ rName } [${ rID }]`;
            setFlyoutTitle(title);
            
            setIsDetailsOpen(true);
        }
    };

    /**
     * Download the production paperwork for the selected run
     */
    const printRunPaperwork = async () => {

        if (!selecteds || selecteds.length > 1) {
            return;
        }

        const req = new DocumentProductionPaperwork(selecteds[0].id);
        dispatch(getDocumentHelper(req));
        
    };

    /**
     * Insert the provided list of rows at a certain point in the list.  The rows will be inserted 
     * based on after and before indexes provided.  A query is made to the backend to get the updated
     * lexo ranks and then updates are called against each run with it's new rank
     * @param tmpFullRows The collection of selected rows we're inserting
     * @param beforeIndex The run index that we want to insert items before
     * @param afterIndex  The run index that we want to insert items after
     * @param insertQuantity The number of rows we're inserting.  Used to query for lexo ranks
     */
    const onRowInsert = async (tmpFullRows : any[], beforeIndex : number|undefined, afterIndex : number|undefined, insertQuantity : number, type : InsertMethod) => {

        let afterRank : string|undefined;
        let beforeRank : string|undefined;

        // Get the before and after run ranks that we need to send to the 
        // backend to get the updated sorts for the new elements
        if (beforeIndex !== undefined && beforeIndex >= 0) {
            beforeRank = runs[beforeIndex].rank;
        }

        if (afterIndex !== undefined && afterIndex >= 0) {
            afterRank = runs[afterIndex].rank;
        }

        const rankingResult = await service.getRankings(insertQuantity, beforeRank, afterRank);
        if (rankingResult.status === StatusCode.Success) {

            const tmpRuns = [...runs];

            for (let i = 0; i < tmpFullRows.length; i++) {

                const runId = tmpFullRows[i].runId;
                const rank = rankingResult.data.rankings[i];

                const result = await service.updateRunRank(runId, rank);
                if (result.status === StatusCode.Success) {

                    const rIdx = tmpRuns.findIndex((itm : ProductionRun) => itm.id === runId);
                    if (rIdx >= 0) {
                       tmpRuns[rIdx].rank = rank;
                    }
                }
            }

            tmpRuns.sort((itmA: ProductionRun, itmB: ProductionRun) => {
                if (itmA.rank < itmB.rank) { return -1; }
                if (itmA.rank > itmB.rank) { return 1; }
                return 0;
            });

            const tmpCells = [...cellData];
            const newCells : any[] = [];
            tmpRuns.forEach((itm: ProductionRun) => { 
                for (let i = 0; i < tmpCells.length; i++) {
                    if (tmpCells[i][0].parentRunId === itm.id) {
                        newCells.push(tmpCells[i]);
                    }
                }
            });
    
            onClearSelections(tmpCells, tmpRuns);
            setRuns(tmpRuns);
            setCellData(newCells);
            setSelecteds([]);
        
        }
    };

    /**
     * Update the page state based on the selection events coming from
     * the metric table
     * @param cells Represents the cell metrics that were selected
     * @param fullRows Represents any full rows that hae been selected
     */
    const onCellChange = (cells : any[], fullRows: any[]) => {
        setCellData(cells);

        const totalV2 = new MetricRunningTotal();
        cells.forEach((itm : CellMetric[]) => {

            itm.forEach((citm : CellMetric, cellIndx : number) => {

                if (cellIndx < 3) {
                    return;
                }

                if (citm.isCurrentlySelected === true && citm.jobType !== undefined && citm.jobType >= 0) {
                    for (let i = 0; i < totalV2.METRIC_LIST.length; i++) {
                        if (totalV2.METRIC_LIST[i].jobType === citm.jobType) {
                            totalV2.METRIC_LIST[i].quantity += citm.quantity;
                            break;
                        }
                    }
                }
            });

        });

        if (fullRows.length > 0) {            
            const tmpRuns : any[] = [];
            fullRows.forEach((itm : any) => (
                tmpRuns.push(runs[itm.index])
            ));

            setSelecteds(tmpRuns);
        }

        setSelectedTotal(totalV2);
        setShowRowTotal(fullRows.length > 0);
    };

    /**
     * Clear all the selections.  Amounts to setting isChecked
     * and isCurrentlySelected to false for everything we know about
     * which should propogate down to the table and refresh
     * @param cellOverride a cellData collection to clear out, if not provided use the local state
     * @param runOverride a run collection to clear out, if not provided use the local state
     */
    const onClearSelections = (cellOverride : any[]|undefined, runOverride : any[]|undefined) => {

        const totalV2 = new MetricRunningTotal();
        setSelectedTotal(totalV2);

        const tmpCells = cellOverride ? cellOverride : [...cellData];
        tmpCells.forEach((itm : any) => {
            itm.forEach((sitm: CellMetric) => {
                sitm.isCurrentlySelected = false;
            });
        });

        if (!cellOverride) {
            setCellData(tmpCells);
        }
        
        const tmpRuns = runOverride ? runOverride : [...runs];
        tmpRuns.forEach((itm: ProductionRun) => {
            itm.isChecked = false;
        });

        if (!runOverride) {
            setRuns(tmpRuns);
        }
    };

    /**
     * Iterate through selected cells and build objects that need updated
     * @param compareStatus Which is the status we consider when selecting cells
     * @returns A list of ProductionJobUpdate that
     */
    const getSelectedJobUpdates = (tmpCells: any, compareStatus: ProductionJobStatus) => {
        const selectedJobs : ProductionJobUpdate[] = [];

        const addToSelectedJobs = (jobId:string, runId: string|number) => {
            const updateVM = new ProductionJobUpdate();
            updateVM.jobId = jobId;
            updateVM.runId = runId;

            selectedJobs.push(updateVM);
        };

        for (let i = 0; i < tmpCells.length; i++) {

            for (let j = 0; j < tmpCells[i].length; j++) {

                const currentCell : CellMetric = tmpCells[i][j];
                if (currentCell.isCurrentlySelected === true) {                    
                    const jobId = currentCell.value;
                    const runId = currentCell.parentRunId;

                    if (runId && runId > 0 && jobId && jobId > 0 && currentCell.status === compareStatus) {                            
                        addToSelectedJobs(jobId, runId);                        
                    }
                }
            }        
        }

        return selectedJobs;
    }

    /**
     * Process the API results from moving jobs to printed or completed
     * @param tmpCells The cell collection we're updating
     * @param results The results from the API call
     * @param selectedJobs The original selected jobs sent for updates
     * @param newStatus The status we should set the cells to when a match is found
     */
    const handleJobUpdateResults = (tmpCells: any, results: ApiResponse<ProductionJobUpdateResult[]>, selectedJobs: ProductionJobUpdate[], newStatus: ProductionJobStatus) => {
        if (results.status === StatusCode.Success) {
            results.data.forEach((updatedResultItem : ProductionJobUpdateResult) => {
                if (updatedResultItem.status === StatusCode.Success) {                    
                    for (let i = 0; i < tmpCells.length; i++) {

                        // This row runID matches the run ID for job we're looking at, 
                        // so we can look at jobs for this row
                        if (tmpCells[i][0].parentRunId === updatedResultItem.runId) {
                            // Iterate over all the cells in a row
                            for (let j = 0; j < tmpCells[i].length; j++) {                                    
                                if((tmpCells[i][j].cellType === CellType.Metric) && 
                                   (tmpCells[i][j].value === updatedResultItem.jobId)) {
                                    tmpCells[i][j].status = newStatus;                                        
                                }

                                tmpCells[i][j].isCurrentlySelected = false;
                            }
                        }
                    }
                    
                }
            });
        }

        onClearSelections(tmpCells, undefined);
        setCellData(tmpCells);
        setSelecteds([]);
    }

    /**
     * Loop through jobs that are selected and call complete for that job
     */
    const onCompleteSelections = async () => {
        const tmpCells = [...cellData];
        const selectedJobs = getSelectedJobUpdates(tmpCells, ProductionJobStatus.Printed);

        if (selectedJobs.length > 0) {
            const results = await service.markJobComplete(selectedJobs);
            handleJobUpdateResults(tmpCells, results, selectedJobs, ProductionJobStatus.Complete);
        }
        else {
            console.info('No jobs meet the criteria to complete.')
        }
    };

    /**
     * Loop through jobs that are selected and call print for that job
     */
    const onPrintSelections = async () => {
        const tmpCells = [...cellData];
        const selectedJobs = getSelectedJobUpdates(tmpCells, ProductionJobStatus.Open);

        if (selectedJobs.length > 0) {
            const results = await service.markJobPrinted(selectedJobs);
            handleJobUpdateResults(tmpCells, results, selectedJobs, ProductionJobStatus.Printed);            
        }
        else {
            console.info('No jobs meet the criteria to print.')
        }
    };

    /**
     * Temporarily mark the fullRows as being cut
     * @param fullRows The rows to cut
     */
    const onRowsCut = (fullRows : any[]) => {
        const tmpRuns = [...runs];

        tmpRuns.forEach((runItem : ProductionRun) => {

            runItem.isChecked = false;

            for (let i = 0; i < fullRows.length; i++) {

                if (runItem.id === fullRows[i].runId) {
                    runItem.isChecked = true;
                    break;
                }
            }

        });

        setRuns(tmpRuns);
    };

    /**
     * Cancel the selected rows, putting them back into the planning pages
     * @param runIds A list of run IDs that need to be cancelled
     */
    const onCancelRuns = async (runIds : number[]) => {

        const result = await ResultDialog.show({
            title: 'Cancel Run(s)?',
            message: 'Are you sure you want to cancel the selected runs?'
        });

        if (result) {
            const runsToRemove = [];
            for (let i = 0; i < runIds.length; i++) {            
                const result = await service.cancelProductionRun(runIds[i]);
                if (result.status === StatusCode.Success) {
                    runsToRemove.push(runIds[i]);
                }
            }

            removeFullRows(runsToRemove);
        }        
    };

    /**
     * Complete the selected rows.  If shipments, this will trigger the shipment
     * to update it's status to read to ship.
     * @param runIds A list of run IDs that need to be completed
     */
    const onCompleteRuns = async (runIds : number[]) => {

        const result = await ResultDialog.show({
            title: 'Complete Run(s)?',
            message: 'Are you sure you want to complete the selected runs?'
        });

        if (result) {
            const runsToRemove = [];
            for (let i = 0; i < runIds.length; i++) {
                const run = runs.find((itm : ProductionRun) => itm.id === runIds[i]);
                if (run !== undefined) {
                    const result = await service.completeProductionRun(runIds[i]);
                    if (result.status === StatusCode.Success) {
                        runsToRemove.push(runIds[i]);
                    }
                }
            }

            removeFullRows(runsToRemove);
        }
    };

    const onDownloadSelections = async () => {
        const tmpCells = [...cellData];
        const selectedItems = {} as any;

        const checkAndAddToDictionary = (sitm: CellMetric) => {
            if(selectedItems.hasOwnProperty(sitm.jobType))
            {
                selectedItems[sitm.jobType!].push(sitm.parentRunId);
            }
            else {
                selectedItems[sitm.jobType!] = [sitm.parentRunId];
            }
        }

        for (let i = 0; i < tmpCells.length; i++) {

            for (let j = 0; j < tmpCells[i].length; j++) {

                const sitm : CellMetric = tmpCells[i][j];
                if (sitm.isCurrentlySelected === true && sitm.jobType !== ProductionJobType.NotApplicable) {
                    checkAndAddToDictionary(sitm);
                }
            }
        }

        if(!AppUtils.IsEmpty(selectedItems))
        {
            const req = new DocumentProductionPaperworkByLine(selectedItems);
            dispatch(getDocumentHelper(req));

            setCellData(tmpCells);
            setSelecteds([]);
        }

    }

    /**
     * Remove full rows of table from the base run collection as well
     * as the cell data collection.
     * @param runsToRemove The list of run IDs to remove from the table
     */
    const removeFullRows = (runsToRemove : number[]) => {

        const tmpRuns = [...runs];
        const tmpCells = [...cellData];

        runsToRemove.forEach((itm : number) => {
            
            const runIdx = tmpRuns.findIndex((run : ProductionRun) => {
                return run.id === itm;
            });

            if (runIdx >= 0) {
                tmpRuns.splice(runIdx, 1);
            }

            const cellRunIdx = tmpCells.findIndex((runCell : any) => {
                return runCell[0].parentRunId === itm;
            });

            if (cellRunIdx >= 0) {
                tmpCells.splice(cellRunIdx, 1);
            }

        });

        setRuns(tmpRuns);
        setCellData(tmpCells);
        setSelecteds([]);

    };

    const exportSchedule = () => {
        const req = new ProductionScheduleExport();
        dispatch(getDocumentHelper(req));        
    };

    return (
        <PageContent
            classes="production-module"
            pageName={ showMetrics ? PageNames.PRODUCTION_SCHEDULE_CDOEM : PageNames.PRODUCTION_SCHEDULE_RS }>
            <PageHeading shouldStick={ true }>
                <div className="planning-button-list schedule-actions">
                    <MetricTableLegend inline={ true } />
                    {
                        // The export is of the CD/OEM schedule
                        showMetrics
                            ? <HasPermission requiredPermissions={[
                                Permissions.ProductionScheduleView
                            ]}>
                                <button 
                                    onClick={ exportSchedule }
                                    className="btn new attention">
                                    Export
                                </button>
                            </HasPermission>
                            : null
                    }
                </div>
            </PageHeading>
            <PageBody>
                
                <MetricTable
                    runs={ runs }
                    cells={ cellData }
                    onRowsCut={ onRowsCut }
                    onRowInsert={ onRowInsert }
                    clearSelections={ onClearSelections }
                    printSelections={ onPrintSelections }
                    downloadSelections={ onDownloadSelections }
                    completeSelections={ onCompleteSelections }
                    onCancelSelectedRows={ onCancelRuns }
                    onCompleteSelectedRows={ onCompleteRuns }
                    onViewDetails={ openDetailFlyout }
                    onPrintPaperwork={ printRunPaperwork }
                    showLegend={ false }
                    loadingOnly={ !showMetrics }
                    onCellChange={ onCellChange }
                    headers={['Run ID', 'Name', 'Shipment ID']}
                    nonMetricPropCount={3}
                    loadLabel="MISC"
                    onPrintAllJobs={ onPrintSelections }
                    onCompleteAllJobs={ onCompleteSelections }
                />

                <MetricTotals 
                    totals={ selectedTotal} 
                    rowSelected={ showRowTotal }
                    shouldShow={ showMetrics }
                    loadLabel="MISC"
                />

                <MetricsDetailFlyout
                    orders={ orders }
                    title={ flyoutTitle }
                    type={ PlanningType.Schedule }
                    isOpen={ isDetailsOpen }
                    onClose={ closeDetailFlyout }
                />

            </PageBody>
        </PageContent>
    );
};

/**
 * The schedule of the runs with a Complete Door or an OEM order
 */
export const ProductionScheduleScene = () => <ProductionSchedule group="cdoem" />;
